import IconButton from 'components/common/iconButton/IconButton'
import IconRender from 'components/common/IconRender'
import { useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { CustomColorsRef, IconSizes } from 'types/general'

interface PropertySharePopupProps {
  isOpen: boolean
  onClose: () => void
  shareUrl: string
  message?: string
  anchorRef: React.RefObject<HTMLElement | null>
}

const PANEL_MIN_W = 260

type ShareAction = {
  id: string
  label: string
  icon: string
  onClick: (e: React.MouseEvent) => void
}

const PropertySharePopup = ({
  isOpen,
  onClose,
  shareUrl,
  message,
  anchorRef,
}: PropertySharePopupProps) => {
  const { t } = useTranslation()
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const shareMessage =
    message ?? String(t('propertyCard.shareMessage', { url: shareUrl }))
  const shareTitle = String(t('propertyCard.shareTitle'))

  useLayoutEffect(() => {
    if (!isOpen) {
      setPos(null)
      return
    }

    const anchor = anchorRef?.current
    if (!anchor) {
      setPos(null)
      return
    }

    const update = () => {
      const r = anchor.getBoundingClientRect()
      const left = Math.max(
        8,
        Math.min(r.left, window.innerWidth - PANEL_MIN_W - 8)
      )
      setPos({ top: r.bottom + 8, left })
    }

    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [isOpen, anchorRef])

  if (!isOpen || !pos) return null

  const encodedText = encodeURIComponent(`${shareMessage} ${shareUrl}`)
  const encodedUrl = encodeURIComponent(shareUrl)

  const openShareWindow = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer')
    onClose()
  }

  const actions: ShareAction[] = [
    {
      id: 'copy',
      label: String(t('propertyCard.shareCopyLink')),
      icon: 'regular-copy',
      onClick: e => {
        e.stopPropagation()
        e.preventDefault()
        void (async () => {
          try {
            await navigator.clipboard.writeText(shareUrl)
            toast.success(String(t('propertyCard.shareLinkCopied')))
            setTimeout(onClose, 100)
          } catch {
            toast.error(String(t('propertyCard.shareLinkCopyFailed')))
          }
        })()
      },
    },
    {
      id: 'sms',
      label: String(t('propertyCard.shareSms')),
      icon: 'regular-message',
      onClick: e => {
        e.stopPropagation()
        e.preventDefault()
        openShareWindow(`sms:?body=${encodedText}`)
      },
    },
    {
      id: 'email',
      label: String(t('propertyCard.shareEmail')),
      icon: 'regular-envelope',
      onClick: e => {
        e.stopPropagation()
        e.preventDefault()
        openShareWindow(
          `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodedText}`
        )
      },
    },
    {
      id: 'whatsapp',
      label: String(t('propertyCard.shareWhatsapp')),
      icon: 'brands-whatsapp',
      onClick: e => {
        e.stopPropagation()
        e.preventDefault()
        openShareWindow(`https://api.whatsapp.com/send?text=${encodedText}`)
      },
    },
    {
      id: 'facebook',
      label: String(t('propertyCard.shareFacebook')),
      icon: 'brands-facebook',
      onClick: e => {
        e.stopPropagation()
        e.preventDefault()
        openShareWindow(
          `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`
        )
      },
    },
  ]

  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    actions.push({
      id: 'native',
      label: String(t('propertyCard.shareNative')),
      icon: 'regular-share-nodes',
      onClick: e => {
        e.stopPropagation()
        e.preventDefault()
        void navigator
          .share({ title: shareTitle, text: shareMessage, url: shareUrl })
          .then(() => onClose())
          .catch(() => undefined)
      },
    })
  }

  const panel = (
    <div
      className="fixed z-[100] rounded-xl border border-gray-100 bg-white p-3 shadow-xl"
      style={{ top: pos.top, left: pos.left, minWidth: PANEL_MIN_W }}
      onClick={e => e.stopPropagation()}
      onKeyDown={e => e.stopPropagation()}
      role="dialog"
      aria-label={shareTitle}
    >
      <div className="mb-2 flex items-center justify-between gap-2 px-1">
        <span className="text-sm font-semibold text-imovSecondary">{shareTitle}</span>
        <IconButton
          icon="regular-circle-xmark"
          size={IconSizes.MD}
          color={CustomColorsRef.Primary}
          hoverColor={CustomColorsRef.Danger}
          handleClick={e => {
            e.stopPropagation()
            onClose()
          }}
        />
      </div>

      <ul className="flex flex-col gap-0.5">
        {actions.map(action => (
          <li key={action.id}>
            <button
              type="button"
              onClick={action.onClick}
              className="flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left text-sm text-imovSecondary transition-colors hover:bg-imovPrimary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-imovPrimary"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-100">
                <IconRender
                  icon={action.icon}
                  size={IconSizes.MD}
                  color={CustomColorsRef.Primary}
                />
              </span>
              <span className="font-medium">{action.label}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  )

  if (typeof document === 'undefined') return null
  return createPortal(panel, document.body)
}

export default PropertySharePopup
